import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

export interface TablePaginationProps {
  total: number;
  page: number;
  size: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onSizeChange?: (size: number) => void;
  sizeOptions?: number[];
  /** How many numbered page buttons to show around the current page. Default: 5. */
  windowSize?: number;
  className?: string;
  /**
   * Layout variant: false (default) puts the total count and page controls
   * side by side (total left, controls right) in a single row. true stacks
   * them - total right-aligned on its own row above the centered page
   * controls - matching the look of Tabulator's native footer (total via
   * `footerElement`, paginator centered below it) used by ReportManagement
   * and the asset-management CRUD screens.
   */
  centered?: boolean;
}

/**
 * Single shared pagination control for server-driven list tables. Pair with
 * `useServerTable` + a `{rows,total,total_pages}` response. Tabulator's own
 * pagination should be turned off (`pagination: false`) when this is used.
 */
const TablePagination = ({
  total,
  page,
  size,
  totalPages,
  onPageChange,
  onSizeChange,
  sizeOptions = [10, 20, 50, 100],
  windowSize = 5,
  className = '',
  centered = false,
}: TablePaginationProps) => {
  const { t } = useTranslation();

  const lastPage = Math.max(totalPages, 1);
  const current = Math.min(Math.max(page, 1), lastPage);

  // Build a sliding window of page numbers centered on the current page.
  const half = Math.floor(windowSize / 2);
  let start = Math.max(1, current - half);
  const end = Math.min(lastPage, start + windowSize - 1);
  start = Math.max(1, end - windowSize + 1);
  const pages: number[] = [];
  for (let p = start; p <= end; p++) pages.push(p);

  const go = (p: number) => {
    const target = Math.min(Math.max(p, 1), lastPage);
    if (target !== current) onPageChange(target);
  };

  const btn = (active: boolean, disabled?: boolean) =>
    cn(
      'min-w-[32px] h-[32px] px-2 rounded text-sm cursor-pointer select-none',
      'border border-[#E6E6E6] dark:border-[#2b465f]',
      disabled && 'opacity-40 cursor-not-allowed',
      active
        ? 'bg-[#135A78] text-white font-bold dark:bg-[#1e3043] dark:text-[#9cc8dc]'
        : 'bg-[#FEFEFE] text-[#6A6A6A] dark:bg-[#1a2535] dark:text-[#808890] hover:opacity-80'
    );

  const totalLabel = (
    <span className="text-sm text-au-neutral-4 whitespace-nowrap">
      {t('cmn > total')} {total} {t('cmn > results')}
    </span>
  );

  // Default variant is icon-only (compact, used by 11+ other list tables);
  // the centered variant (report-management-style) shows the words instead,
  // matching Tabulator's native pager text.
  const firstLabel = centered ? t('cmn > first page') : '«';
  const prevLabel = centered ? t('cmn > page before') : '‹';
  const nextLabel = centered ? t('cmn > next page') : '›';
  const lastLabel = centered ? t('cmn > last page') : '»';

  // Tabulator's native pager only boxes the numbered page buttons - First /
  // Prev / Next / Last render as plain text with no border. Mirrored here
  // only for the centered variant; the default (icon) variant keeps every
  // control boxed.
  const navBtn = (disabled?: boolean) =>
    centered
      ? cn(
          'h-[32px] px-2 rounded text-sm cursor-pointer select-none',
          'text-[#6A6A6A] dark:text-[#808890] hover:opacity-80',
          disabled && 'opacity-40 cursor-not-allowed'
        )
      : btn(false, disabled);

  const pageControls = (
    <div className="flex items-center gap-[6px]">
      <button
        type="button"
        className={navBtn(current <= 1)}
        disabled={current <= 1}
        onClick={() => go(1)}
        title={t('cmn > first page')}
        aria-label={t('cmn > first page')}
      >
        {firstLabel}
      </button>
      <button
        type="button"
        className={navBtn(current <= 1)}
        disabled={current <= 1}
        onClick={() => go(current - 1)}
        title={t('cmn > page before')}
        aria-label={t('cmn > page before')}
      >
        {prevLabel}
      </button>

      {pages.map((p) => (
        <button
          key={p}
          type="button"
          className={btn(p === current)}
          onClick={() => go(p)}
          aria-current={p === current ? 'page' : undefined}
        >
          {p}
        </button>
      ))}

      <button
        type="button"
        className={navBtn(current >= lastPage)}
        disabled={current >= lastPage}
        onClick={() => go(current + 1)}
        title={t('cmn > next page')}
        aria-label={t('cmn > next page')}
      >
        {nextLabel}
      </button>
      <button
        type="button"
        className={navBtn(current >= lastPage)}
        disabled={current >= lastPage}
        onClick={() => go(lastPage)}
        title={t('cmn > last page')}
        aria-label={t('cmn > last page')}
      >
        {lastLabel}
      </button>

      {onSizeChange && (
        <select
          className="ml-2 h-[32px] px-2 rounded text-sm border border-[#E6E6E6] dark:border-[#2b465f] bg-[#FEFEFE] dark:bg-[#1a2535] cursor-pointer"
          value={size}
          onChange={(e) => onSizeChange(Number(e.target.value))}
        >
          {sizeOptions.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      )}
    </div>
  );

  if (centered) {
    return (
      <div className={cn('flex flex-col items-center gap-[6px] w-full py-[10px]', className)}>
        <div className="w-full flex justify-end">{totalLabel}</div>
        {pageControls}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-4 w-full py-[10px] flex-wrap',
        className
      )}
    >
      {totalLabel}
      {pageControls}
    </div>
  );
};

export default TablePagination;
